'use client';

import { useCallback } from 'react';

import useIsMobile from 'hooks/useIsMobile';

/**
 * Opens the HubSpot scheduler for /private in a new tab.
 *
 * Replaces the embedded iframe modal, which silently failed (~30/month) whenever a
 * visitor's ad-blocker or tracking protection blocked the third-party `hubspot.com`
 * frame. HubSpot itself permits framing (no X-Frame-Options, report-only CSP), so the
 * failures were environmental and could not be fixed in-frame. A top-level navigation in
 * a new tab is not subject to third-party frame blocking and is the path the old fallback
 * already relied on.
 *
 * `window.open` runs synchronously inside the click handler — before the async analytics
 * import — so it stays within the user-gesture context and is never killed by popup blockers.
 *
 * @param {string} calendarUrl - The HubSpot meetings URL to open.
 */
const useCalendarBooking = (calendarUrl) => {
  const isMobile = useIsMobile();

  const openCalendar = useCallback(async () => {
    window.open(calendarUrl, '_blank', 'noopener,noreferrer');
    try {
      const { trackEvent } = await import('utils/analytics');
      trackEvent('private_calendar_opened', {
        calendar_url: calendarUrl,
        is_mobile: isMobile,
      });
    } catch {
      // analytics must never block the user flow
    }
  }, [calendarUrl, isMobile]);

  return { openCalendar };
};

export default useCalendarBooking;
